import { IRoomValidationStatusCode } from "@backend/src/shared/schemas/schemas";
import { createSelector } from "@reduxjs/toolkit";
import { ROOM_VALIDATION_STATUS_LABELS } from "../../constantsFromBackend";
import { ROOM_VALIDATION_STATUS_COLORS } from "../../RoomColoring";
import { formatShortDate } from "../../globalUtils";
import { selectUniqueStandardRoomValidationsKeys } from "../../Room/drofusOccurrenceValidationColoringSelectors";

export interface IValidationColumnMeta {
  /** Unique and stable id, also used as ag-Grid colId */
  id: string;
  /** Column header shown in the grid */
  header: string;
  /** Path used by valueGetter to extract the value */
  valuePath: string[];
  /** Optional tooltip text */
  tooltip?: string;
  /** Optional valueFormatter – används för datum och namn */
  valueFormatter?: (value: unknown) => string;

  /** Optional helper for cell styling (e.g. status colors) */
  valueToColor?: (value: unknown) => string | undefined;
}

/** Header-hjälp för alla standardromskolumner */
const headerForKey = (key: `standardRoom.${string}`, suffix: string): string => {
  const discipline = key.split(".")[1] ?? "";
  return `RI${discipline} ${suffix}`;
};

/** Stabilt colId för ag-Grid */
const colIdForKey = (key: `standardRoom.${string}`, field: string): string =>
  `${key}.decision.${field}`;

const formatStatus = (value: unknown): string => {
  if (!value) return "–";
  const code = value as IRoomValidationStatusCode;
  return ROOM_VALIDATION_STATUS_LABELS[code] ?? "–";
};

const statusColor = (value: unknown): string | undefined => {
  if (!value) return undefined;
  const code = value as IRoomValidationStatusCode;
  return ROOM_VALIDATION_STATUS_COLORS[code];
};

export const selectValidationColumnMetas = createSelector(
  [selectUniqueStandardRoomValidationsKeys],
  (keys): IValidationColumnMeta[] => {
    let columns: IValidationColumnMeta[] = [];

    keys.forEach((key) => {
      const discipline = key.split(".")[1] ?? "";

      // 1) Match rate (som før)
      columns.push({
        id: `${key}.summary.matchRatePct`,
        header: headerForKey(key as `standardRoom.${string}`, "andel matchet standardrom"),
        valuePath: ["validation", "standardRoom", discipline, "summary", "matchRatePct"],
        tooltip: "Treffsikkerhet basert på standardrom-validering (0–100 %).",
        valueFormatter: (value: unknown) => {
          const n = typeof value === "number" ? value : Number(value);
          return Number.isFinite(n) ? `${n.toFixed(1)} %` : "–";
        },
      });

      // 2) Status (label + farge i cellRenderer via valueToColor)
      columns.push({
        id: colIdForKey(key as `standardRoom.${string}`, "status"),
        header: headerForKey(key as `standardRoom.${string}`, "status"),
        valuePath: ["validationDecisions", "standardRoom", discipline, "current", "status"],
        tooltip: "Manuell status for standardrom-validering.",
        valueFormatter: formatStatus,
        valueToColor: statusColor,
      });

      // 3) Status satt av (navn)
      columns.push({
        id: colIdForKey(key as `standardRoom.${string}`, "updatedByUserName"),
        header: headerForKey(key as `standardRoom.${string}`, "status satt av"),
        valuePath: [
          "validationDecisions",
          "standardRoom",
          discipline,
          "current",
          "updatedByUserName",
        ],
        tooltip: "Hvem som sist satte statusen.",
        valueFormatter: (value: unknown) =>
          typeof value === "string" && value.length > 0 ? value : "–",
      });

      // 4) Status dato/tid
      columns.push({
        id: colIdForKey(key as `standardRoom.${string}`, "updatedAt"),
        header: headerForKey(key as `standardRoom.${string}`, "status dato"),
        valuePath: ["validationDecisions", "standardRoom", discipline, "current", "updatedAt"],
        tooltip: "Når statusen sist ble oppdatert.",
        valueFormatter: (value: unknown) => {
          if (!value) return "–";
          if (typeof value === "string") {
            try {
              return formatShortDate(value);
            } catch {
              return value;
            }
          }
          return "–";
        },
      });
    });

    return columns;
  }
);
